import consumer from './consumer';
import { get } from '@rails/request.js';
import { admin_chats_conversations_path } from '../../javascript/routes';
import { createIcons, icons } from 'lucide';

let retryCount = 0;
const maxRetries = 3;
const retryInterval = 3000;
let refreshTimeout = null;
let updateTimeout = null;

consumer.subscriptions.create('ConversationsChannel', {
  connected() {
    retryCount = 0;
  },

  disconnected() {
    this.reconnect();
  },

  received(data) {
    const currentUserId = document.querySelector('meta[name="current-user-id"]')?.getAttribute('content');

    if ((data.user_id === currentUserId && data.action === 'message_created') || data.action === 'ping') {
      return;
    }
    const params = Object.fromEntries(new URLSearchParams(window.location.search));

    const currentConversationId =
      params?.conversation_id ||
      document.querySelector('#chat-area')?.dataset?.conversationId ||
      document.querySelector('[data-conversation-id]')?.dataset?.conversationId;

    switch (data.action) {
      case 'message_created': {
        this.updateConversationList(currentConversationId);

        const isCurrentConversation = String(currentConversationId) === String(data.conversation_id);
        const messagesList = document.getElementById('messages-list');

        if (messagesList && isCurrentConversation) {
          this.refreshConversationMessages(data);
        }
        break;
      }

      case 'conversation.created/updated':
        this.updateConversationList(currentConversationId);
        break;

      case 'message_read':
        this.updateMessageReadStatus(data.user_id, !!data.seen);
        this.updateUnreadCount(data.total_unread_count);
        break;
      default:
        if (
          data.conversation_id &&
          params?.conversation_id === data.conversation_id &&
          data.user_id !== currentUserId
        ) {
          this.refreshConversationMessages(data.conversation_id, data);
        }
        break;
    }
  },

  updateUnreadCount(count) {
    const unreadCountElement = document.getElementById('unread_messages_count');
    const chatMenuItem = document.querySelector('a[href*="chats/conversations"]');

    if (count && count > 0) {
      if (unreadCountElement) {
        unreadCountElement.textContent = count > 99 ? '99+' : count;
        unreadCountElement.style.display = 'inline-flex';
      } else if (chatMenuItem) {
        const badge = document.createElement('span');
        badge.id = 'unread_messages_count';
        badge.className =
          'inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-500 rounded-full';
        badge.textContent = count > 99 ? '99+' : count;
        chatMenuItem.appendChild(badge);
      }
    } else {
      if (unreadCountElement) {
        unreadCountElement.style.display = 'none';
      }
    }
  },

  async updateConversationList(conversationId) {
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }

    updateTimeout = setTimeout(async () => {
      try {
        await get(admin_chats_conversations_path({ conversation_id: conversationId }), {
          responseKind: 'turbo-stream',
        });
      } catch (error) {
        console.error('Error updating conversation list:', error);
      }
    }, 100);
  },

  async refreshConversationMessages(messageData) {
    const messagesList = document.getElementById('messages-list');
    if (messagesList && messageData) {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }

      refreshTimeout = setTimeout(() => {
        this.appendMessageToList(messageData, messagesList);
      }, 100);
    }
  },

  appendMessageToList(messageData, messagesList) {
    const existingMessage = document.getElementById(`message-${messageData.id}`);
    if (existingMessage) {
      return;
    }

    const messageElement = this.createMessageElement(messageData);

    if (!messageElement) return;

    const emptyState = document.getElementById('empty-state');
    if (emptyState) {
      emptyState.remove();
    }

    messagesList.appendChild(messageElement);

    const chatAreaController = this.application?.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="chat-area"]'),
      'chat-area',
    );
    if (chatAreaController) {
      chatAreaController.scrollToBottom();
    }
  },

  createMessageElement(messageData) {
    const currentUserId = document.querySelector('meta[name="current-user-id"]')?.getAttribute('content');
    const isCurrentUser = String(messageData.sender_id) === String(currentUserId);

    const messageDiv = document.createElement('div');
    messageDiv.className = 'message-item mt-2';
    messageDiv.id = `message-${messageData.id}`;
    messageDiv.setAttribute('data-controller', 'icons messages');

    if (!isCurrentUser) {
      // Other user message (left side)
      const senderInitial = messageData.sender_name ? messageData.sender_name.charAt(0).toUpperCase() : '?';
      const avatarHtml = messageData.sender_avatar
        ? `<img src="${messageData.sender_avatar}" alt="${messageData.sender_name || 'User'}" class="w-8 h-8 rounded-full object-cover">`
        : `<span class="text-gray-600 font-medium text-xs">${senderInitial}</span>`;

      messageDiv.innerHTML = `
        <div class="flex items-start space-x-3">
          <div class="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0 ${messageData.sender_avatar ? '' : 'bg-gray-300'}">
            ${avatarHtml}
          </div>
          
          <div class="flex flex-col space-y-1">
            <div class="flex items-center space-x-2">
              <span class="text-xs text-gray-500">${messageData.sender_name || 'Unknown'}</span>
              <span class="text-xs text-gray-500">${this.formatTime(messageData.created_at)}</span>
            </div>
            
            <div class="bg-white rounded-2xl px-4 py-3 shadow-sm max-w-md border border-gray-100">
              ${messageData.content ? `<p class="text-sm text-gray-900">${this.escapeHtml(messageData.content)}</p>` : ''}
              ${this.renderAttachments(messageData.attachments)}
            </div>
          </div>
        </div>
      `;
    } else {
      return null;
    }

    return messageDiv;
  },

  renderAttachments(attachments) {
    if (!attachments || attachments.length === 0) {
      return '';
    }

    return `
      <div class="mt-2 space-y-2" data-controller="preview-file">
        ${attachments
          .map((attachment) => {
            const isImage = attachment.content_type?.startsWith('image/');
            const isVideo = attachment.content_type?.startsWith('video/');
            const fileSize = this.formatFileSize(attachment.byte_size);

            if (isImage) {
              return `
              <div class="attachment-item text-right">
                <div class="inline-block relative rounded-lg overflow-hidden max-w-xs">
                  <img src="${attachment.url}" 
                       alt="${this.escapeHtml(attachment.filename)}" 
                       class="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity" 
                       data-action="click->preview-file#showImage">
                  
                  <a href="${attachment.url}" 
                     class="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-70 transition-opacity" 
                     download="${attachment.filename}">
                    <i class="text-sm" data-lucide="download"></i>
                  </a>
                  
                  <div class="text-xs text-gray-500 mt-1">
                    ${this.escapeHtml(attachment.filename)} (${fileSize})
                  </div>
                </div>
              </div>
            `;
            } else if (isVideo) {
              return `
              <div class="attachment-item">
                <div class="inline-block relative rounded-lg overflow-hidden max-w-xs">
                  <video class="w-full h-auto" controls preload="metadata">
                    <source src="${attachment.url}" type="${attachment.content_type}">
                    Your browser does not support the video tag.
                  </video>
                  
                  <a href="${attachment.url}" 
                     class="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-70 transition-opacity" 
                     download="${attachment.filename}">
                    <i class="text-sm" data-lucide="download"></i>
                  </a>
                  
                  <div class="text-xs text-gray-500 mt-1">
                    ${this.escapeHtml(attachment.filename)} (${fileSize})
                  </div>
                </div>
              </div>
            `;
            } else {
              const { cssClass, iconName } = this.getAttachmentIcon(attachment);
              return `
              <div class="attachment-item">
                <a href="${attachment.url}" 
                   class="inline-flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 transition-colors rounded-lg p-3 text-sm" 
                   download="${attachment.filename}">
                  <i class="${cssClass}" data-lucide="${iconName}"></i>
                  
                  <div class="flex flex-col">
                    <span class="font-medium text-gray-900">${this.escapeHtml(attachment.filename)}</span>
                    <span class="text-xs text-gray-500">${fileSize}</span>
                  </div>
                </a>
              </div>
            `;
            }
          })
          .join('')}
      </div>
    `;
  },

  getAttachmentIcon(attachment) {
    const contentType = attachment.content_type || '';
    const filename = attachment.filename || '';
    const extension = filename.split('.').pop()?.toLowerCase();

    if (contentType.includes('pdf') || extension === 'pdf') {
      return { cssClass: 'w-4 h-4 text-red-500', iconName: 'file-text' };
    } else if (contentType.includes('word') || ['doc', 'docx'].includes(extension)) {
      return { cssClass: 'w-4 h-4 text-blue-500', iconName: 'file-text' };
    } else if (contentType.includes('excel') || ['xls', 'xlsx'].includes(extension)) {
      return { cssClass: 'w-4 h-4 text-green-500', iconName: 'file-spreadsheet' };
    } else if (contentType.includes('powerpoint') || ['ppt', 'pptx'].includes(extension)) {
      return { cssClass: 'w-4 h-4 text-orange-500', iconName: 'file-text' };
    } else if (
      contentType.includes('zip') ||
      contentType.includes('archive') ||
      ['zip', 'rar', '7z'].includes(extension)
    ) {
      return { cssClass: 'w-4 h-4 text-purple-500', iconName: 'archive' };
    } else {
      return { cssClass: 'w-4 h-4 text-gray-500', iconName: 'file' };
    }
  },

  formatFileSize(bytes) {
    if (!bytes) return '0 B';

    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  },

  formatTime(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  },

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  },

  reconnect() {
    if (retryCount < maxRetries) {
      retryCount++;
      setTimeout(() => {
        consumer.subscriptions.create('ConversationsChannel', this);
      }, retryInterval);
    }
  },

  updateMessageReadStatus(readerUserId, seen) {
    try {
      const msgEls = document.querySelectorAll(`.message-${readerUserId}`);
      if (!msgEls || msgEls.length === 0) return;

      msgEls.forEach((msgEl) => {
        try {
          const icon = msgEl.querySelector('svg');
          if (icon) icon.remove();

          const newIcon = document.createElement('i');
          newIcon.setAttribute('data-lucide', seen ? 'check-check' : 'check');
          newIcon.classList.add(seen ? 'text-blue-500' : 'text-gray-400');

          msgEl.children[0].children[0].appendChild(newIcon);
        } catch (innerErr) {
          console.error('Failed to update message read icon for one message:', innerErr);
        }
      });

      createIcons({ icons });
    } catch (e) {
      console.error('Failed to update message read status:', e);
    }
  },
});
